import { useEffect, useRef, useState } from "react";
import shaka from "shaka-player";
import { Loader2 } from "lucide-react";

interface Props {
  src: string;
  /** "live" tunes for low-latency streaming; "recording" for VOD. */
  type?: "live" | "recording";
  autoplay?: boolean;
  controls?: boolean;
  muted?: boolean;
  poster?: string;
  /** Watermark text (e.g. viewer name / id) overlaid + repositioned to deter screen capture. */
  watermark?: string;
}

const ShakaPlayer = ({
  src,
  type = "recording",
  autoplay = true,
  controls = true,
  muted,
  poster,
  watermark,
}: Props) => {
  const isLive = type === "live";
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<shaka.Player | null>(null);

  const [error, setError] = useState("");
  const [buffering, setBuffering] = useState(true);
  const [playing, setPlaying] = useState(false);
  // Watermark position (percent) — moved periodically so it can't be cropped out.
  const [wmPos, setWmPos] = useState({ top: 12, left: 12 });

  useEffect(() => {
    let active = true;
    shaka.polyfill.installAll();

    if (!shaka.Player.isBrowserSupported()) {
      setError("This browser does not support protected video playback.");
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    const player = new shaka.Player(video);
    playerRef.current = player;

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    video.addEventListener("play", onPlay);
    video.addEventListener("playing", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onPause);

    player.configure({
      abr: { enabled: true },
      streaming: {
        lowLatencyMode: isLive,
        bufferingGoal: isLive ? 6 : 30,
        rebufferingGoal: isLive ? 1 : 2,
        bufferBehind: isLive ? 8 : 30,
      },
    });

    player.addEventListener("error", (e: any) => {
      console.error("Shaka Error:", e?.detail);
      if (active) setError("Unable to load this video stream.");
    });
    player.addEventListener("buffering", (e: any) => {
      if (active) setBuffering(Boolean(e?.buffering));
    });

    player
      .load(src)
      .then(() => {
        if (!active) return;
        setError("");
        setBuffering(false);
        // Live: jump to the live edge.
        if (isLive && player.isLive()) {
          const range = player.seekRange();
          if (range?.end) video.currentTime = range.end;
        }
        if (autoplay) {
          video.muted = muted ?? true; // browsers block sound autoplay
          video.play().catch(() => undefined);
        }
      })
      .catch((e) => {
        console.error(e);
        if (active) setError("Unable to load this video stream.");
      });

    return () => {
      active = false;
      video.removeEventListener("play", onPlay);
      video.removeEventListener("playing", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onPause);
      void player.destroy();
      playerRef.current = null;
    };
  }, [src, isLive]);

  // Reposition watermark every few seconds.
  useEffect(() => {
    if (!watermark) return;
    const move = () =>
      setWmPos({
        top: 8 + Math.random() * 78,
        left: 8 + Math.random() * 66,
      });
    move();
    const id = setInterval(move, 4000);
    return () => clearInterval(id);
  }, [watermark]);

  return (
    <div className="relative h-full w-full overflow-hidden rounded-xl bg-black">
      <video
        ref={videoRef}
        controls={controls}
        muted={muted}
        poster={poster}
        playsInline
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        className="h-full w-full bg-black object-contain"
      />

      {/* LIVE badge */}
      {isLive && !error && (
        <span className="pointer-events-none absolute left-3 top-3 z-20 inline-flex items-center gap-1.5 rounded-md bg-red-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Live
        </span>
      )}

      {/* Moving watermark — only while playing */}
      {watermark && playing && !error && (
        <div
          className="pointer-events-none absolute z-10 select-none text-xs font-semibold text-white/45 drop-shadow transition-all duration-700 sm:text-sm"
          style={{ top: `${wmPos.top}%`, left: `${wmPos.left}%` }}
        >
          {watermark}
        </div>
      )}

      {/* Buffering spinner */}
      {buffering && !error && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <Loader2 className="h-10 w-10 animate-spin text-white/90" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/70 p-5 text-center">
          <div>
            <p className="text-sm font-bold text-white">{error}</p>
            <p className="mt-1 text-xs text-white/60">
              Please try again when the {isLive ? "court stream is live" : "recording is available"}.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShakaPlayer;
